import type { Aabb, Billboard, CommercialTemplateId, District, Plot, StaticCollider, Surface, WorldProp } from "@townsy/contracts";
import { YAW_EAST, YAW_NORTH, YAW_SOUTH, YAW_WEST, aabb } from "../geometry";
import type { StreetInfo } from "../manifest";
import { COMMERCIAL_FRONT_Z, COMMERCIAL_PITCH, PLOT_D, ROAD_HALF, SIDE_ROAD_HALF, SIDEWALK, commercialPlot, propColliders, residentialPlot } from "../plots";
import { MARKET_STREET_Z } from "../venues";

/**
 * World v5 (ADR-0041, founder 2026-10-23: "2 new districts", "Mixed like today"): a shopping district beside Central.
 * One generator builds Riverside (east of Central) and Hillcrest (west, the mirror image), repeating today's pattern: a
 * shopping street with residential lanes behind it.
 *
 * World v6 (ADR-0042, founder 2026-10-24: "They need to be attached to the town so you can drive there"): the district
 * is attached to Central by road. Its shopping street continues Market Street at z = 70 (Central builds Market Street's
 * stretch up to the old wall line, manifest.ts), so the street now starts AT the wall line (u = 0) instead of at a
 * gate forecourt (u 12), and the wall toward Central is open for the road and its two sidewalks (z 63..77). No gate,
 * no forecourt; every plot, billboard, the square, the spawn and the overview camera are exactly as in v5
 * (fixtures/world-v5-snapshot.json).
 *
 * Layout, in metres `u` measured OUTWARD from Central's old boundary wall (x = side * (312 + u); z north as everywhere):
 *  - the district spans u 0..320 and z -108..110; its wall toward Central (u = 0) is open for z 63..77, where the
 *    shopping street and its sidewalks come in from Market Street;
 *  - the shopping street runs along z = 70 from u 0 to 308 (6 m road, 4 m sidewalks), like Market Street: 10 shops
 *    on its north side facing south (u 91..229, the 14 m pitch) and 10 on its south side facing north, five either
 *    side of the square (u 77..145 and 175..243); house numbers still count from u 12 (the v5 street end), so no
 *    address changes;
 *  - the square (u 146..174, z 41..63, Central's plaza size) south of the street: fountain, benches and planters; the
 *    spawn and "Return to plaza" point, with a crosswalk over the street to the north row; its billboard stands
 *    behind it facing south (toward the overview camera, as bb-1 / bb-2 do; boards are two-sided, ADR-0023);
 *  - two residential lanes run south from the street at u = 68 and u = 252 (Central's lane cross-section), five
 *    plots per side each (rows at z 28, 4, -20, -44 and, past the back road, -86: Central's lane rows moved up by
 *    70 m), ending at z = -100;
 *  - the back road (z = -65) joins the two lanes in T-junctions, closing a driving loop (street, lane, back road,
 *    lane); the lanes and the back road have their own traffic loops; in the whole town the shopping street is driven
 *    by the loop that comes along Market Street from Central (traffic.ts trafficLoops);
 *  - the far end of the street is closed by the second billboard, facing back along it (as bb-3 / bb-4 do);
 *  - everything behind the north row (z >= 93) is a closed back lot; lamps, lane trees and park trees as in Central.
 * No civic buildings and no demo businesses (ADR-0041 decision 2): every shop and plot starts unowned.
 * Every surface, prop and collider is tagged with the district (Plot / Billboard carry it already).
 */
export interface ShoppingDistrictSpec {
  id: string;
  name: string;
  /** +1: east of Central (u grows with x), -1: west of Central (the mirror image). */
  side: 1 | -1;
  streetName: string;
  /** The residential lanes, the one nearer Central first (its plots take the first ids). */
  laneNames: readonly [string, string];
  backRoadName: string;
  /** Id number of the first shop (north row west to east, then the south row west to east). */
  firstShop: number;
  /** Id number of the first home plot (nearer lane first; per lane west side north to south, then east side). */
  firstPlot: number;
  /** The square's billboard, then the one closing the far end of the street. */
  billboardIds: readonly [string, string];
  /** Facade styles of the north and south rows, west to east (no two neighbours alike). */
  northStyles: readonly CommercialTemplateId[];
  southStyles: readonly CommercialTemplateId[];
}

/** Everything one district adds to the manifest, plus its named streets (manifest.ts StreetInfo). */
export interface DistrictBuild {
  district: District;
  plots: Plot[];
  billboards: Billboard[];
  surfaces: Surface[];
  props: WorldProp[];
  colliders: StaticCollider[];
  streets: StreetInfo[];
}

/** Central's bounds (manifest.ts DISTRICT_BOUNDS x +-312): u = 0 of a shopping district, the line between the districts. */
export const CENTRAL_EDGE_X = 312;
/** The shopping streets run on Market Street's line, z = 70, and continue it (world v6). */
export const SHOPPING_STREET_Z = MARKET_STREET_Z;
/**
 * World v6 (ADR-0042): the opening in the walls between Central and a shopping district, z 63..77: Market Street's /
 * the shopping street's 6 m road and its two 4 m sidewalks. Every other part of every boundary wall stays closed.
 */
export const LINK_CORRIDOR_Z = { min: MARKET_STREET_Z - ROAD_HALF - SIDEWALK, max: MARKET_STREET_Z + ROAD_HALF + SIDEWALK } as const;

// ---- Layout in u (metres outward from Central's wall) -------------------------------------------
const DISTRICT_U = 320;
const MIN_Z = -108;
const MAX_Z = 110;
/** World v6: the street starts at the wall line (v5: u 12, after a gate forecourt); house numbers still count from u 12. */
const STREET_U0 = 0;
const NUMBERING_U0 = 12;
const STREET_U1 = 308;
const NORTH_ROW_U = Array.from({ length: 10 }, (_, k) => 97 + COMMERCIAL_PITCH * k); // 97..223
const SOUTH_ROW_U = [83, 97, 111, 125, 139, 181, 195, 209, 223, 237];
const SQUARE_U = { min: 146, max: 174 } as const;
const SQUARE_U_CENTRE = (SQUARE_U.min + SQUARE_U.max) / 2; // 160
/** The square reaches 22 m south from the street's south sidewalk (Central's plaza: 28 x 22 m). */
const SQUARE_DEPTH = 22;
const LANE_U = [68, 252] as const;
/** Central's lane rows (z -42, -66, -90, -114, -156), back road (-135) and lane end (-170), moved up by 70 m. */
const RES_ROWS_Z = [28, 4, -20, -44, -86] as const;
const BACK_ROAD_Z = -65;
const LANE_END_Z = -100;
/** Crosswalks reach 6.5 m into a lane from the back road's sidewalk line (as in Central). */
const LANE_CROSSWALK_DEPTH = 6.5;
/** The street-end billboard stands 6 m past the road's end (bb-3 / bb-4: 6 m past Main Street). */
const END_BOARD_U = STREET_U1 + 6;
const SQUARE_BOARD_Z_GAP = 2.5;

const snake = (name: string) => name.toLowerCase().replace(/\s+/g, "_");

/** House number on the shopping street (as Market Street, manifest.ts marketStreetNumber): by position from its west end, odd north, even south. */
export function shoppingStreetNumber(x: number, westEndX: number, side: "north" | "south"): number {
  return 2 * Math.round((x - westEndX) / COMMERCIAL_PITCH) + (side === "north" ? 1 : 0);
}

export function shoppingDistrict(spec: ShoppingDistrictSpec): DistrictBuild {
  const s = spec.side;
  const id = spec.id;
  const X = (u: number) => s * (CENTRAL_EDGE_X + u);
  /** A box between two u values (either order once mirrored) and two z values. */
  const R = (u0: number, z0: number, u1: number, z1: number): Aabb => aabb(Math.min(X(u0), X(u1)), z0, Math.max(X(u0), X(u1)), z1);
  const P = (u: number, z: number) => ({ x: X(u), z });
  const bounds = R(0, MIN_Z, DISTRICT_U, MAX_Z);
  const streetZ = SHOPPING_STREET_Z;
  const north0 = streetZ + ROAD_HALF; // 73
  const south0 = streetZ - ROAD_HALF; // 67
  const southWalk = south0 - SIDEWALK; // 63: the street's south sidewalk edge (front line of the south row)
  const northBackZ = streetZ + COMMERCIAL_FRONT_Z + PLOT_D; // 93: back line of the north row

  // ---- Plots ------------------------------------------------------------------------------------
  const streetRoad = R(STREET_U0, south0, STREET_U1, north0);
  /** The v5 street's west end (Riverside u 12, Hillcrest u 308): house numbers count from it, unchanged in v6. */
  const westEndX = Math.min(X(NUMBERING_U0), X(STREET_U1));
  const plots: Plot[] = [];
  let n = spec.firstShop;
  for (const [row, side] of [
    [NORTH_ROW_U, "north"],
    [SOUTH_ROW_U, "south"],
  ] as const) {
    const styles = side === "north" ? spec.northStyles : spec.southStyles;
    const xs = row.map(X).sort((a, b) => a - b); // west to east
    xs.forEach((cx, k) => plots.push(commercialPlot(n++, cx, side, styles[k]!, `${shoppingStreetNumber(cx, westEndX, side)} ${spec.streetName}`, streetZ, id)));
  }
  let r = spec.firstPlot;
  LANE_U.forEach((u, lane) => {
    const sx = X(u);
    let k = 0;
    for (const side of ["west", "east"] as const) {
      for (const cz of RES_ROWS_Z) plots.push(residentialPlot(r++, sx, side, cz, `${++k} ${spec.laneNames[lane]}`, id));
    }
  });

  // ---- Billboards (rentals at the standard day rate; labels live with the rental records) ---------
  const billboards: Billboard[] = [
    { id: spec.billboardIds[0], districtId: id, position: { x: X(SQUARE_U_CENTRE), y: 5, z: southWalk - SQUARE_DEPTH - SQUARE_BOARD_Z_GAP }, facing: YAW_SOUTH, width: 10, height: 4 },
    { id: spec.billboardIds[1], districtId: id, position: { x: X(END_BOARD_U), y: 5, z: streetZ }, facing: s > 0 ? YAW_WEST : YAW_EAST, width: 10, height: 4 },
  ];

  // ---- Surfaces ---------------------------------------------------------------------------------
  const surfaces: Surface[] = [];
  const surface = (kind: Surface["kind"], box: Aabb) => surfaces.push({ kind, aabb: box, districtId: id });
  const square = R(SQUARE_U.min, southWalk - SQUARE_DEPTH, SQUARE_U.max, southWalk);
  surface("road", streetRoad);
  surface("sidewalk", R(STREET_U0, north0, STREET_U1, north0 + SIDEWALK));
  surface("sidewalk", R(STREET_U0, southWalk, STREET_U1, south0));
  surface("plaza", square);
  const backRoad = R(LANE_U[0] + SIDE_ROAD_HALF, BACK_ROAD_Z - ROAD_HALF, LANE_U[1] - SIDE_ROAD_HALF, BACK_ROAD_Z + ROAD_HALF);
  const laneRoads: Aabb[] = [];
  for (const u of LANE_U) {
    // The lane runs from the street's south kerb through the back road's T-junction to a cul-de-sac row.
    const road = R(u - SIDE_ROAD_HALF, LANE_END_Z, u + SIDE_ROAD_HALF, south0);
    laneRoads.push(road);
    surface("road", road);
    surface("sidewalk", R(u - SIDE_ROAD_HALF - SIDEWALK, LANE_END_Z, u - SIDE_ROAD_HALF, southWalk));
    surface("sidewalk", R(u + SIDE_ROAD_HALF, LANE_END_Z, u + SIDE_ROAD_HALF + SIDEWALK, southWalk));
    surface("crosswalk", R(u - SIDE_ROAD_HALF, south0, u + SIDE_ROAD_HALF, north0)); // across the street at the lane
  }
  surface("crosswalk", R(SQUARE_U_CENTRE - ROAD_HALF, south0, SQUARE_U_CENTRE + ROAD_HALF, north0)); // square to north row
  surface("road", backRoad);
  const walkU0 = LANE_U[0] + SIDE_ROAD_HALF + SIDEWALK;
  const walkU1 = LANE_U[1] - SIDE_ROAD_HALF - SIDEWALK;
  surface("sidewalk", R(walkU0, backRoad.maxZ, walkU1, backRoad.maxZ + SIDEWALK));
  surface("sidewalk", R(walkU0, backRoad.minZ - SIDEWALK, walkU1, backRoad.minZ));
  LANE_U.forEach((u, k) => {
    // Junction crosswalks: across the lane north and south of the back road, across the back road on its arm.
    surface("crosswalk", R(u - SIDE_ROAD_HALF, backRoad.maxZ, u + SIDE_ROAD_HALF, backRoad.maxZ + LANE_CROSSWALK_DEPTH));
    surface("crosswalk", R(u - SIDE_ROAD_HALF, backRoad.minZ - LANE_CROSSWALK_DEPTH, u + SIDE_ROAD_HALF, backRoad.minZ));
    const inward = k === 0 ? 1 : -1; // toward the other lane
    const a0 = u + inward * SIDE_ROAD_HALF;
    surface("crosswalk", R(a0, backRoad.minZ, a0 + inward * 2 * ROAD_HALF, backRoad.maxZ));
  });

  // ---- Props ------------------------------------------------------------------------------------
  const props: WorldProp[] = [];
  const prop = (kind: WorldProp["kind"], u: number, z: number, scale = 1, rotation = 0) => props.push({ kind, position: P(u, z), rotation, scale, districtId: id });
  // Street lamps by the kerb, in the alleys of the shop rows and on past them, clear of the crosswalks, the lane
  // mouths and the shop doors (world v6: Market Street's lamps continue them toward Central; the v5 gate lamps are gone).
  for (const u of [20, 48, 76, 104, 132, 188, 216, 244, 272, 300]) prop("lamp", u, north0 + 0.6);
  for (const u of [20, 48, 90, 118, 146, 174, 202, 230, 272, 300]) prop("lamp", u, south0 - 0.6);
  // The square: the fountain, four benches facing it and four planters (Central's plaza arrangement).
  const fountainZ = southWalk - 13;
  prop("fountain", SQUARE_U_CENTRE, fountainZ);
  for (const [du, dz] of [
    [-9, -6],
    [9, -6],
    [-9, -19],
    [9, -19],
  ] as const) {
    const bx = X(SQUARE_U_CENTRE + du);
    // A bench west of the fountain faces east (toward it), whichever way the district is mirrored.
    prop("bench", SQUARE_U_CENTRE + du, southWalk + dz, 1, bx < X(SQUARE_U_CENTRE) ? YAW_EAST : YAW_WEST);
    prop("planter", SQUARE_U_CENTRE + du * 1.3, southWalk + dz + (dz < -10 ? -1.5 : 1.5));
  }
  // Lane trees in the gaps between plots and beside the junctions; lane lamps between rows (alternating sides).
  for (const u of LANE_U) {
    for (const z of [56, 44, 16, -8, -32, -56, -74, -98]) {
      prop("tree", u - SIDE_ROAD_HALF - SIDEWALK - 1.5, z, 0.9);
      prop("tree", u + SIDE_ROAD_HALF + SIDEWALK + 1.5, z, 0.9);
    }
    for (const [z, k] of [
      [40, -1],
      [16, 1],
      [-8, -1],
      [-32, 1],
      [-80, -1],
    ] as const) {
      prop("lamp", u + k * (SIDE_ROAD_HALF + 0.6), z);
    }
  }
  // Back road lamps, clear of the junctions and the U-turns.
  for (const u of [90, 120, 150, 170, 200, 230]) {
    prop("lamp", u, backRoad.maxZ + 0.6);
    prop("lamp", u, backRoad.minZ - 0.6);
  }
  // The park behind the square (between the lanes' inner plots) and the pocket parks at both ends of the street.
  for (const z of [22, 0, -22, -42]) for (const u of [113, 131, 149, 171, 189, 207]) prop("tree", u, z, 1.05);
  for (let k = 0; k < 6; k++) {
    prop("tree", 24 + 12 * k, k % 2 === 0 ? 83 : 88.5, 1.05);
    prop("tree", 236 + 12 * k, k % 2 === 0 ? 83 : 88.5, 1.05);
  }
  for (const z of [54, 44]) {
    for (const u of [22, 34, 46]) prop("tree", u, z, 1.05);
    for (const u of [266, 278, 290, 302]) prop("tree", u, z, 1.05);
  }

  // ---- Colliders --------------------------------------------------------------------------------
  const colliders: StaticCollider[] = [];
  for (const plot of plots) if (plot.type !== "RESIDENTIAL") colliders.push({ id: `bld:${plot.id}`, kind: "building", aabb: plot.buildBounds, districtId: id });
  props.forEach((p, i) => colliders.push(...propColliders(p, i, id)));
  const B = bounds;
  const T = 2;
  const wall = (name: string, box: Aabb) => colliders.push({ id: `bound:${id}:${name}`, kind: "boundary", aabb: box, districtId: id });
  // World v6: the wall toward Central (Riverside's west wall, Hillcrest's east wall) is open for the street and its
  // sidewalks (LINK_CORRIDOR_Z): two pieces, south (":s") and north (":n") of the opening. The far wall stays whole.
  const corridor = LINK_CORRIDOR_Z;
  const [nearName, nearX0, farName, farX0] = s > 0 ? (["w", B.minX - T, "e", B.maxX] as const) : (["e", B.maxX, "w", B.minX - T] as const);
  wall(`${nearName}:s`, aabb(nearX0, B.minZ - T, nearX0 + T, corridor.min));
  wall(`${nearName}:n`, aabb(nearX0, corridor.max, nearX0 + T, B.maxZ + T));
  wall(farName, aabb(farX0, B.minZ - T, farX0 + T, B.maxZ + T));
  wall("s", aabb(B.minX - T, B.minZ - T, B.maxX + T, B.minZ));
  wall("n", aabb(B.minX - T, B.maxZ, B.maxX + T, B.maxZ + T));
  // The closed back lot behind the north row ("bound:backlot*": the renderer fences and plants it).
  colliders.push({ id: `bound:backlot:${id}:n`, kind: "boundary", aabb: aabb(B.minX, northBackZ, B.maxX, B.maxZ), districtId: id });

  // ---- District and streets ---------------------------------------------------------------------
  const spawnZ = southWalk - 4; // 4 m into the square from the sidewalk (Central: z -11, 4 m in from -7)
  const anchor = (du: number, z: number) => ({ position: P(SQUARE_U_CENTRE + du, z), facing: YAW_NORTH });
  const district: District = {
    id,
    name: spec.name,
    bounds,
    spawn: anchor(0, spawnZ),
    // Central's return anchors around the spawn, moved to the square.
    returnAnchors: [anchor(-4, spawnZ), anchor(4, spawnZ), anchor(0, spawnZ - 4), anchor(-6, spawnZ - 5), anchor(6, spawnZ - 5), anchor(0, spawnZ + 2.5)],
    // Looking north over the square at the north row's fronts (both rows' rooftop boards are two-sided).
    overviewCamera: { target: { x: X(SQUARE_U_CENTRE), y: 7, z: streetZ + 2 }, alpha: -Math.PI / 2, beta: 1.08, radius: 100 },
    square,
    // World v6 (ADR-0042): no gates; the district is reached along the road (the v5 gates and their pairing are retired).
    gates: [],
  };
  const lanes = LANE_U.map((u, k) => ({ x: X(u), name: spec.laneNames[k]!, road: laneRoads[k]! })).sort((a, b) => a.x - b.x);
  const streets: StreetInfo[] = [
    { id: snake(spec.streetName), name: spec.streetName, kind: "street", roads: [streetRoad], from: { x: streetRoad.minX, z: streetZ }, to: { x: streetRoad.maxX, z: streetZ } },
    ...lanes.map((l): StreetInfo => ({ id: snake(l.name), name: l.name, kind: "lane", roads: [l.road], from: { x: l.x, z: l.road.maxZ }, to: { x: l.x, z: l.road.minZ } })),
    { id: snake(spec.backRoadName), name: spec.backRoadName, kind: "back", roads: [backRoad], from: { x: backRoad.minX, z: BACK_ROAD_Z }, to: { x: backRoad.maxX, z: BACK_ROAD_Z } },
  ];
  return { district, plots, billboards, surfaces, props, colliders, streets };
}

/** The z of a shopping district's home rows (plot centres), north to south. */
export const SHOPPING_DISTRICT_ROWS_Z: readonly number[] = RES_ROWS_Z;
